import React, { useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import { Sparkles, Moon, Zap } from 'lucide-react';
import { GlassCard } from '../SharedUI';
import { sounds } from '../../services/soundService';
import type { OperationMode } from '@/contexts/AppContext';
import type { ThemeId } from '@/contexts/themeRegistry';

export function ThemeWidget({
  t,
  lang,
  theme,
  setTheme,
  operationMode,
  onModeChange,
}: {
  t?: any;
  lang: 'en' | 'zh';
  theme: string;
  setTheme: (value: ThemeId) => void;
  operationMode: OperationMode;
  onModeChange: (mode: OperationMode) => void;
}) {
  const themeOptions = useMemo(() => [
    {
      id: 'celestial',
      label: t?.苍穹 || '星辉',
      mode: 'chat' as OperationMode,
      modeLabel: t?.modeChat || (lang === 'zh' ? '聊天' : 'Chat'),
      accessLabel: lang === 'zh' ? '请求批准' : 'Request Approval',
      icon: <Sparkles size={16} />,
      glow: 'from-celestial-saturn/35 to-cyan-300/20',
      orb: 'from-celestial-saturn to-cyan-200',
      line: 'bg-celestial-saturn',
    },
    {
      id: 'nebula',
      label: t?.nebula || '星云',
      mode: 'assistant' as OperationMode,
      modeLabel: t?.modeAssistant || (lang === 'zh' ? '助手' : 'Assistant'),
      accessLabel: lang === 'zh' ? '替我审批' : 'Approve For Me',
      icon: <Moon size={16} />,
      glow: 'from-indigo-500/35 to-fuchsia-400/20',
      orb: 'from-indigo-500 to-fuchsia-400',
      line: 'bg-indigo-400',
    },
    {
      id: 'cyber',
      label: t?.cyber || '赛博',
      mode: 'autonomous' as OperationMode,
      modeLabel: t?.modeAutonomy || t?.modeAutoExecute || (lang === 'zh' ? '自主' : 'Autonomy'),
      accessLabel: lang === 'zh' ? '完全访问' : 'Full Access',
      icon: <Zap size={16} />,
      glow: 'from-emerald-400/30 to-teal-300/20',
      orb: 'from-emerald-400 to-teal-300',
      line: 'bg-emerald-400',
    },
  ], [t?.苍穹, t?.nebula, t?.cyber, t?.modeChat, t?.modeAssistant, t?.modeAutonomy, t?.modeAutoExecute, lang]);

  const handleThemeSelect = useCallback((option: typeof themeOptions[number]) => {
    if (option.mode !== 'autonomous' || operationMode === 'autonomous') {
      setTheme(option.id as ThemeId);
    }
    onModeChange(option.mode);
    sounds.playPulse();
  }, [operationMode, setTheme, onModeChange]);

  return (
    <GlassCard className="rounded-[1.6rem] border-white/5 bg-black/20 p-3">
      <div className="grid grid-cols-3 gap-3">
        {themeOptions.map((option) => {
          const active = theme === option.id && operationMode === option.mode;
          const visualActive = theme === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleThemeSelect(option)}
              className={`group relative min-h-[134px] overflow-hidden rounded-[1.25rem] border p-3 text-left transition-all ${
                active
                  ? 'border-white/20 bg-white/[0.10] shadow-[0_18px_45px_rgba(0,0,0,0.28)]'
                  : visualActive
                    ? 'border-white/10 bg-white/[0.06]'
                    : 'border-white/5 bg-black/20 hover:bg-white/[0.05]'
              }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${option.glow} transition-opacity group-hover:opacity-80 ${visualActive ? 'opacity-100' : 'opacity-40'}`} />
              <div className="relative flex h-full flex-col justify-between">
                <div className="flex items-start justify-between gap-2">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${option.orb} text-black shadow-lg`}>
                    {option.icon}
                  </div>
                  <span className={`h-2 w-2 rounded-full ${active ? option.line : 'bg-white/20'}`} />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.14em] text-white/85">
                    {option.label}
                  </div>
                  <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white/45">
                    {option.modeLabel}
                  </div>
                  <div className="mt-2 min-h-[22px] rounded-lg border border-white/10 bg-black/20 px-2 py-1 text-[10px] font-black uppercase tracking-[0.08em] text-white/70">
                    {option.accessLabel}
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-white/10">
                    <motion.div
                      animate={{ width: active ? '100%' : visualActive ? '64%' : '28%' }}
                      className={`h-full rounded-full ${option.line}`}
                    />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </GlassCard>
  );
}
